import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, CreditCard } from "lucide-react";
import { SiGitconnected } from "react-icons/si";
import { VscDebugDisconnect } from "react-icons/vsc";
import { FiCheck, FiArrowLeft, FiLock, FiPhone } from "react-icons/fi";
import "react-phone-number-input/style.css";
import PhoneInput, { getCountries, isValidPhoneNumber } from "react-phone-number-input";
import instagram from "../../assets/instagram_onb.png";
import facebook from "../../assets/facebook_onb.png";
import whatsapp from "../../assets/whatsapp_onb.png";
import telegram from "../../assets/telegram_onb.png";
import calendar from "../../assets/google_calendar_onb.png";
import { REST_API_URL } from "../../api/config";
import { useAppDispatch, useAppSelector } from "../../hooks/redux/useRedux";
import { selectCheckPayment } from "../../redux/slices/paymentSlice/PaymentSlice";
import { getLocationThunk } from "../../redux/slices/profileSlice/asyncthunk";
import { selectLocationCode } from "../../redux/slices/profileSlice/ProfileSlice";

const API = REST_API_URL;
 

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

async function authFetch(url, options = {}) {
  let access =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");
  let refresh =
    localStorage.getItem("refreshToken") ||
    sessionStorage.getItem("refreshToken");

  const doRequest = async (token) => {
    const headers = buildHeaders(token, options.headers || {});

    return fetch(url, {
      ...options,
      headers,
    });
  };

  if (!access) {
    return { status: 401, ok: false, json: async () => ({}) };
  }

  let response = await doRequest(access);

  if (response.status !== 401) return response;

  if (!refresh) return response;

  const refreshHeaders = {
    "Content-Type": "application/json",
    "User-Timezone": getUserTimeZone(),
  };

  const refreshRes = await fetch(`${API}/auth/token/refresh/`, {
    method: "POST",
    headers: refreshHeaders,
    body: JSON.stringify({ refresh }),
  });

  const refreshData = await refreshRes.json().catch(() => ({}));

  if (refreshRes.ok && refreshData.access) {
    access = refreshData.access;
    localStorage.setItem("accessToken", access);
    return doRequest(access);
  }

  if (!refreshRes.ok) {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
  }

  return response;
}

function InstagramIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img
          src={instagram}
          alt="Instagram"
          className="w-7 h-7 object-contain"
        />
      </div>
    </div>
  );
}

function WhatsAppIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-green-500 to-green-600 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img src={whatsapp} alt="WhatsApp" className="w-7 h-7 object-contain" />
      </div>
    </div>
  );
}

function TelegramIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-blue-400 to-blue-500 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img src={telegram} alt="Telegram" className="w-7 h-7 object-contain" />
      </div>
    </div>
  );
}

function FacebookIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-blue-600 to-blue-700 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img src={facebook} alt="Facebook" className="w-7 h-7 object-contain" />
      </div>
    </div>
  );
}

function CalendarIcon({ connected }) {
  return (
    <div
      className={`w-12 h-12 flex items-center justify-center rounded-xl ${connected ? "bg-gradient-to-br from-blue-500 to-blue-600 p-1" : "bg-gray-50"}`}
    >
      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
        <img
          src={calendar}
          alt="Google Calendar"
          className="w-7 h-7 object-contain"
        />
      </div>
    </div>
  );
}

const instagramUrl = (email) =>
  `https://www.instagram.com/oauth/authorize?enable_fb_login=0&force_authentication=1&disable_native=1&client_id=1362107724965082&redirect_uri=${API}/auth/connect-instagram&response_type=code&scope=instagram_business_basic,instagram_business_manage_messages&state=${encodeURIComponent(
    email,
  )}|||web`;

const whatsappUrl = (email) =>
  `https://www.facebook.com/v23.0/dialog/oauth?display=popup&client_id=1167918065008974&redirect_uri=${encodeURIComponent(
    "https://testing.halper.ai/api/whatsapp/connect-whatsapp/",
  )}&config_id=1412479926475322&response_type=code&auth_type=&fallback_redirect_uri=${encodeURIComponent(
    "https://testing.halper.ai/api/whatsapp/connect-whatsapp/",
  )}&override_default_response_type=true&extras=%7B%22featureType%22%3A%22whatsapp_business_app_onboarding%22%2C%22sessionInfoVersion%22%3A%223%22%2C%22version%22%3A%22v3%22%2C%22features%22%3A%5B%7B%22name%22%3A%22marketing_messages_lite%22%7D%2C%7B%22name%22%3A%22app_only_install%22%7D%5D%7D&state=${encodeURIComponent(
    email + "|||web",
  )}`;

const googleCalendarUrl = (email) => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/profile`);
  return `${API}/calendar/google/calendar-init?state=${encodeURIComponent(email + "|||web")}&redirect_uri=${redirectUri}`;
};

function DisconnectModal({ isOpen, onClose, onConfirm, channelName }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleConfirm = () => {
    setIsVisible(false);
    setTimeout(onConfirm, 300);
  };

  if (!isOpen && !isVisible) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 transition-all duration-300 ${
        isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl transition-all duration-300 ${
          isVisible
            ? "scale-100 opacity-100 translate-y-0"
            : "scale-95 opacity-0 translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <VscDebugDisconnect className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="font-bold text-2xl text-gray-900 mb-2">
            Disconnect {channelName}?
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            This will stop all automated interactions with your {channelName}{" "}
            account. You can reconnect anytime.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 px-6 py-3.5 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-all duration-200 active:scale-[0.98]"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-6 py-3.5 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl shadow-lg hover:shadow-red-200 transition-all duration-200 active:scale-[0.98]"
          >
            Disconnect
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChannelsAndAppsNew() {
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState("");
  const [social, setSocial] = useState({
    instagram: null,
    whatsapp: null,
    googleCalendar: null,
  });
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const [showSubModal, setShowSubModal] = useState(false);
  const [disconnectModal, setDisconnectModal] = useState({
    isOpen: false,
    channel: null,
    channelName: "",
  });
  const [isCalendarConnecting, setIsCalendarConnecting] = useState(false);

  const [telegramModal, setTelegramModal] = useState({
    isOpen: false,
    step: "phone",
    connectionId: null,
    message: "",
    needPassword: false,
  });
  const [telegramPhone, setTelegramPhone] = useState("");
  const [telegramOtp, setTelegramOtp] = useState("");
  const [telegramPassword, setTelegramPassword] = useState("");
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramError, setTelegramError] = useState(null);
  const locationCode = useAppSelector(selectLocationCode);

  const openTelegramModal = () => {
    setTelegramModal({ isOpen: true, step: "phone", connectionId: null, message: "", needPassword: false });
    setTelegramPhone("");
    setTelegramOtp("");
    setTelegramPassword("");
    setTelegramError(null);
  };

  const closeTelegramModal = () => {
    setTelegramModal((prev) => ({ ...prev, isOpen: false }));
    setTelegramError(null);
  };

  const handleTelegramPhoneSubmit = async () => {
    if (!telegramPhone.trim() || !isValidPhoneNumber(telegramPhone)) return;
    setTelegramLoading(true);
    setTelegramError(null);
    try {
      const res = await authFetch(`${API}/telegram/connections/`, {
        method: "POST",
        body: JSON.stringify({ phone_number: telegramPhone.trim() }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        await fetchSocial();
        closeTelegramModal();
        return;
      }

      if (!res.ok) {
        setTelegramError(data?.error || data?.message || "Invalid phone number. Please try again.");
        return;
      }

      setTelegramModal((prev) => ({
        ...prev,
        step: "otp",
        connectionId: data.connection_id,
        message: data.message || "",
        needPassword: false,
      }));
    } catch (err) {
      setTelegramError("Failed to send request. Please try again.");
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramOtpSubmit = async () => {
    if (telegramOtp.length < 5) return;
    setTelegramLoading(true);
    setTelegramError(null);
    try {
      const res = await authFetch(
        `${API}/telegram/connections/${telegramModal.connectionId}/verify-code/`,
        { method: "POST", body: JSON.stringify({ code: telegramOtp }) },
      );
      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        await fetchSocial();
        closeTelegramModal();
        return;
      }

      if (res.status === 210) {
        // Wrong code — stay on OTP step
        setTelegramError(data?.message || data?.error || "Invalid code. Please try again.");
        setTelegramOtp("");
        return;
      }

      if (!res.ok) {
        // API error (400, 404, etc.) — show error, stay on OTP
        setTelegramError(data?.error || data?.message || "Invalid code. Please try again.");
        setTelegramOtp("");
        return;
      }

      // Success response but not 201 — Telegram requires 2FA password
      setTelegramModal((prev) => ({
        ...prev,
        step: "2fa",
        connectionId: data.connection_id ?? prev.connectionId,
        message: data.message || "",
      }));
      setTelegramOtp("");
    } catch (err) {
      setTelegramError("Invalid code. Please try again.");
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleTelegramPasswordSubmit = async () => {
    if (!telegramPassword.trim()) return;
    setTelegramLoading(true);
    setTelegramError(null);
    try {
      const res = await authFetch(
        `${API}/telegram/connections/${telegramModal.connectionId}/verify-2fa/`,
        { method: "POST", body: JSON.stringify({ password: telegramPassword }) },
      );
      if (res.ok) {
        await fetchSocial();
        closeTelegramModal();
      } else {
        const data = await res.json().catch(() => ({}));
        setTelegramError(data?.error || "Wrong password. Please try again.");
      }
    } catch (err) {
      setTelegramError("Failed to verify. Please try again.");
    } finally {
      setTelegramLoading(false);
    }
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const param = queryParams.get("param");
    const code = queryParams.get("code");
    const error = queryParams.get("error");
    const success = queryParams.get("success");
    const calendarConnected = queryParams.get("calendar_connected");

    const isInstagramSuccess =
      location.pathname.includes("instagram-connect") && param === "success";
    const isWhatsappSuccess =
      location.pathname.includes("whatsapp-connect") && param === "success";

    const hasCalendarParams = code || error || success || calendarConnected;
    const isCalendarCallback =
      location.pathname === "/profile" && hasCalendarParams;

    if (isInstagramSuccess || isWhatsappSuccess) {
      if (typeof window.gtag === "function") {
        window.gtag("event", "social_media_added", {
          social_media_type: isInstagramSuccess ? "Instagram" : "WhatsApp",
        });
      }

      if (isInstagramSuccess) {
        navigate(location.pathname, { replace: true });
        fetchSocial();
      } else if (isWhatsappSuccess) {
        navigate(location.pathname, { replace: true });
        fetchSocial();
      }
    }

    if (isCalendarCallback) {
      if (error) {
        console.error("Google Calendar auth error:", error);
        setIsCalendarConnecting(false);
        alert(`Google Calendar connection failed: ${error}`);
      } else if (code) {
        handleGoogleCalendarCodeExchange(code);
      } else if (success === "true" || calendarConnected === "true") {
        fetchCalendarStatus();
        setIsCalendarConnecting(false);

        setTimeout(() => {
          alert("Google Calendar successfully connected!");
        }, 500);
      }

      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);

      setTimeout(() => {
        fetchCalendarStatus();
      }, 1000);
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    dispatch(getLocationThunk());

    const fetchUserData = async () => {
      const [userRes] = await Promise.all([
        authFetch(`${API}/auth/userinfo/`),
      ]);

      if (userRes.ok) {
        const userData = await userRes.json();
        if (userData.user && userData.user.email) {
          setEmail(userData.user.email);
        }
        setUserInfo(userData);
      } else {
        console.error("Failed to fetch user info:", userRes.status);
      }

      await fetchSocial();
      await fetchCalendarStatus();
      setIsLoading(false);
    };

    fetchUserData();
  }, [dispatch]);

  const fetchSocial = async () => {
    const res = await authFetch(`${API}/auth/social-profile-view/`);

    if (res.ok) {
      const json = await res.json();
      setSocial((prev) => ({ ...prev, ...json }));
    } else {
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const res = await authFetch(`${API}/calendar/google/calendar-exist/`);

      if (res.ok) {
        try {
          const data = await res.json();

          let isConnected = false;

          if (typeof data === "boolean") {
            isConnected = data;
          } else if (typeof data === "object") {
            if (data.existing !== undefined) {
              isConnected = data.existing === true;
            } else if (data.exists !== undefined) {
              isConnected = data.exists === true;
            } else if (data.connected !== undefined) {
              isConnected = data.connected === true;
            } else if (
              data.id !== undefined &&
              data.id !== null &&
              data.id !== ""
            ) {
              isConnected = true;
            } else if (data.status === "connected") {
              isConnected = true;
            }
          }

          const status = { existing: isConnected, ...data };
          setCalendarStatus(status);
          setSocial((prev) => ({ ...prev, googleCalendar: status }));
          return status;
        } catch (parseError) {
          if (res.status === 200) {
            const status = { existing: true };
            setCalendarStatus(status);
            setSocial((prev) => ({ ...prev, googleCalendar: status }));
            return status;
          } else {
            const status = { existing: false };
            setCalendarStatus(status);
            setSocial((prev) => ({ ...prev, googleCalendar: status }));
            return status;
          }
        }
      } else if (res.status === 404) {
        const status = { existing: false };
        setCalendarStatus(status);
        setSocial((prev) => ({ ...prev, googleCalendar: status }));
        return status;
      } else {
        const status = { existing: false };
        setCalendarStatus(status);
        setSocial((prev) => ({ ...prev, googleCalendar: status }));
        return status;
      }
    } catch (error) {
      console.error("Error fetching calendar status:", error);
      const status = { existing: false };
      setCalendarStatus(status);
      setSocial((prev) => ({ ...prev, googleCalendar: status }));
      return status;
    }
  };

  const handleGoogleCalendarCodeExchange = async (code) => {
    try {
      setIsCalendarConnecting(true);

      const response = await authFetch(
        `${API}/calendar/google/token-exchange/`,
        {
          method: "POST",
          body: JSON.stringify({
            code,
            redirect_uri: `${window.location.origin}/profile`,
          }),
        },
      );

      if (response.ok) {
        await fetchCalendarStatus();
        setIsCalendarConnecting(false);

        setTimeout(() => {
          alert("Google Calendar successfully connected!");
        }, 500);
      } else {
        console.error("Token exchange failed:", response.status);
        setIsCalendarConnecting(false);
        alert("Failed to connect Google Calendar. Please try again.");
      }
    } catch (error) {
      console.error("Error exchanging Google OAuth code:", error);
      setIsCalendarConnecting(false);
      alert("Error connecting Google Calendar. Please try again.");
    }
  };

  const disconnectInstagram = async () => {
    await authFetch(`${API}/auth/instagram-disconnect/`, { method: "DELETE" });
    fetchSocial();
  };

  const disconnectTelegram = async () => {
    await authFetch(`${API}/telegram/disconnect-telegram/`, { method: "DELETE" });
    fetchSocial();
  };

  const disconnectWhatsapp = async () => {
    await authFetch(`${API}/whatsapp/disconnect-whatsapp/`, {
      method: "DELETE",
    });
    fetchSocial();
  };

  const disconnectGoogleCalendar = async () => {
    try {
      const res = await authFetch(
        `${API}/calendar/google/calendar-disconnect/`,
        {
          method: "DELETE",
        },
      );

      if (res.ok) {
        await fetchCalendarStatus();
        alert("Google Calendar disconnected successfully");
      } else {
        console.error("Failed to disconnect Google Calendar:", res.status);
        alert("Failed to disconnect Google Calendar. Please try again.");
      }
    } catch (error) {
      console.error("Error disconnecting Google Calendar:", error);
      alert("Error disconnecting Google Calendar. Please try again.");
    }
  };

  const openDisconnectModal = (channel, channelName) => {
    setDisconnectModal({
      isOpen: true,
      channel,
      channelName,
    });
  };

  const closeDisconnectModal = () => {
    setDisconnectModal({
      isOpen: false,
      channel: null,
      channelName: "",
    });
  };

  const handleDisconnectConfirm = () => {
    if (disconnectModal.channel === "instagram") {
      disconnectInstagram();
    } else if (disconnectModal.channel === "whatsapp") {
      disconnectWhatsapp();
    } else if (disconnectModal.channel === "telegram") {
      disconnectTelegram();
    } else if (disconnectModal.channel === "googleCalendar") {
      disconnectGoogleCalendar();
    }
    closeDisconnectModal();
  };

  const handleCalendarConnect = () => {
    if (!email) {
      console.error("Email is required for Google Calendar connection");
      alert("Please wait for user data to load or refresh the page");
      return;
    }

    setIsCalendarConnecting(true);

    const calendarConnectUrl = googleCalendarUrl(email);
    window.location.href = calendarConnectUrl;
  };

  const isCalendarConnected = () => {
    if (!social.googleCalendar) return false;
    const status = social.googleCalendar;
    if (status && typeof status === "object") {
      return status.existing === true || status.exists === true;
    }
    return false;
  };

  const withSubCheck = (connectFn) => () => {
    if (!paymentCheck?.has_active_subscription) {
      setShowSubModal(true);
      return;
    }
    connectFn();
  };

  if (isLoading) {
    return (
      <div className="w-full bg-white rounded-2xl p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded-lg w-48 mb-8"></div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                <div className="flex-1">
                  <div className="h-6 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-24"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const calendarConnected = isCalendarConnected();

  return (
    <>
      <AnimatePresence>
        {showSubModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setShowSubModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-xl w-[340px] p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowSubModal(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              >
                <X size={18} />
              </button>

              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-amber-100 mb-4">
                <CreditCard size={22} className="text-amber-500" />
              </div>

              <h3 className="text-[16px] font-bold text-[#302B2B] mb-2">
                Subscription Required
              </h3>
              <p className="text-sm text-gray-500 mb-5">
                You need an active subscription to connect channels and apps.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubModal(false)}
                  className="flex-1 py-2 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full bg-white rounded-2xl p-6 shadow-sm">
        <div className="mb-8">
          <h2 className="font-normal text-2xl text-gray-900 mb-2">
            Connected Channels
          </h2>
          <p className="text-gray-500 text-sm">
            Manage your social media integrations
          </p>
        </div>

        <div className="space-y-4 mb-10">
          <SectionHeader title="Social Medias" />

          <ChannelItem
            icon={<InstagramIcon connected={!!social.instagram} />}
            label="Instagram"
            description="Connect your business account"
            connected={!!social.instagram}
            onConnect={withSubCheck(
              () => email && window.open(instagramUrl(email), "_blank"),
            )}
            onDisconnect={() => openDisconnectModal("instagram", "Instagram")}
          />

          <ChannelItem
            icon={<WhatsAppIcon connected={!!social.whatsapp} />}
            label="WhatsApp Business"
            description="Connect your WhatsApp Business"
            connected={!!social.whatsapp}
            onConnect={withSubCheck(
              () => email && window.open(whatsappUrl(email), "_blank"),
            )}
            onDisconnect={() => openDisconnectModal("whatsapp", "WhatsApp")}
          />

          <ChannelItem
            icon={<TelegramIcon connected={!!social.telegram} />}
            label="Telegram"
            description="Connect your Telegram account"
            connected={!!social.telegram}
            onConnect={withSubCheck(openTelegramModal)}
            onDisconnect={() => openDisconnectModal("telegram", "Telegram")}
          />

          <ChannelItem
            icon={<FacebookIcon connected={false} />}
            label="Facebook"
            description="Coming soon"
            disabled
          />
        </div>

        <div className="space-y-4">
          <SectionHeader title="Apps & Integrations" />

          <ChannelItem
            icon={<CalendarIcon connected={calendarConnected} />}
            label="Google Calendar"
            description="Sync your calendar events"
            connected={calendarConnected}
            onConnect={withSubCheck(handleCalendarConnect)}
            onDisconnect={() =>
              openDisconnectModal("googleCalendar", "Google Calendar")
            }
            action={
              isCalendarConnecting
                ? "Connecting..."
                : calendarConnected
                  ? "Connected"
                  : "Connect"
            }
            isLoading={isCalendarConnecting}
          />
        </div>
      </div>

      <DisconnectModal
        isOpen={disconnectModal.isOpen}
        onClose={closeDisconnectModal}
        onConfirm={handleDisconnectConfirm}
        channelName={disconnectModal.channelName}
      />

      <TelegramConnectModal
        modal={telegramModal}
        locationCode={locationCode}
        phone={telegramPhone}
        otp={telegramOtp}
        password={telegramPassword}
        loading={telegramLoading}
        error={telegramError}
        onPhoneChange={(value) => {
          setTelegramPhone(value);
          if (telegramError) {
            setTelegramError(null);
          }
        }}
        onOtpChange={setTelegramOtp}
        onPasswordChange={setTelegramPassword}
        onPhoneSubmit={handleTelegramPhoneSubmit}
        onOtpSubmit={handleTelegramOtpSubmit}
        onPasswordSubmit={handleTelegramPasswordSubmit}
        onClose={closeTelegramModal}
      />
    </>
  );
}

function SectionHeader({ title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
      <span className="font-semibold text-gray-500 text-sm uppercase tracking-wider px-4">
        {title}
      </span>
      <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
    </div>
  );
}

function ChannelItem({
  icon,
  label,
  description,
  connected,
  action,
  disabled,
  isLoading,
  onConnect,
  onDisconnect,
}) {
  return (
    <div
      className={`group flex items-center justify-between p-4 rounded-2xl transition-all duration-300 hover:shadow-lg ${
        disabled
          ? "bg-gray-50/50 cursor-not-allowed"
          : connected
            ? "bg-gradient-to-r from-blue-50/30 to-indigo-50/30 hover:from-blue-50/50 hover:to-indigo-50/50"
            : "bg-gray-50/30 hover:bg-gray-50/50"
      }`}
    >
      <div className="flex items-center gap-4">
        {icon}
        <div>
          <h3
            className={`font-semibold text-lg ${
              disabled ? "text-gray-400" : "text-gray-900"
            }`}
          >
            {label}
          </h3>
          <p
            className={`text-sm mt-0.5 ${
              disabled ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {description}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {disabled && (
          <span className="px-3 py-1.5 bg-gray-100 text-gray-400 text-sm font-medium rounded-lg">
            Coming Soon
          </span>
        )}

        {connected && !disabled && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg">
              <FiCheck className="w-4 h-4" />
              <span className="text-sm font-medium">Connected</span>
            </div>
            <button
              onClick={onDisconnect}
              className="px-4 py-2.5 bg-transparent hover:bg-red-50 text-red-600 hover:text-red-700 rounded-xl font-medium text-sm flex items-center gap-2 transition-all duration-200 active:scale-[0.98]"
            >
              <VscDebugDisconnect className="w-4 h-4" />
              Disconnect
            </button>
          </div>
        )}

        {!connected && !disabled && (
          <button
            onClick={onConnect}
            disabled={isLoading}
            className={`px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg hover:shadow-blue-100 flex items-center gap-2 transition-all duration-300 active:scale-[0.98] group/btn ${
              isLoading ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            <span>{isLoading ? "Connecting..." : action || "Connect"}</span>
            {!isLoading && action !== "Connecting..." && (
              <SiGitconnected className="w-4 h-4 transition-transform duration-300 group-hover/btn:rotate-90" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function TelegramConnectModal({
  modal,
  locationCode,
  phone,
  otp,
  password,
  loading,
  error,
  onPhoneChange,
  onOtpChange,
  onPasswordChange,
  onPhoneSubmit,
  onOtpSubmit,
  onPasswordSubmit,
  onClose,
}) {
  if (!modal.isOpen) return null;
  const isPhoneValid = Boolean(phone) && isValidPhoneNumber(phone);
  const normalizedLocationCode = String(locationCode?.country_code || "").toUpperCase();
  const defaultCountry = getCountries().includes(normalizedLocationCode)
    ? normalizedLocationCode
    : undefined;

  const stepConfig = {
    phone: {
      icon: <FiPhone className="w-7 h-7 text-blue-500" />,
      iconBg: "bg-blue-50",
      title: "Connect Telegram",
      subtitle: "Enter your Telegram phone number to connect your account",
    },
    otp: {
      icon: (
        <img src={telegram} alt="Telegram" className="w-7 h-7 object-contain" />
      ),
      iconBg: "bg-blue-50",
      title: "Enter Verification Code",
      subtitle: modal.message || "We sent a code to your Telegram app",
    },
    "2fa": {
      icon: <FiLock className="w-7 h-7 text-indigo-500" />,
      iconBg: "bg-indigo-50",
      title: "Two-Factor Authentication",
      subtitle: modal.message || "Enter your Telegram 2FA password",
    },
  };

  const cfg = stepConfig[modal.step];

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (modal.step === "phone") onPhoneSubmit();
    else if (modal.step === "otp") onOtpSubmit();
    else if (modal.step === "2fa") onPasswordSubmit();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {modal.step !== "phone" && (
              <button
                onClick={() =>
                  onPhoneChange
                    ? (() => {
                        if (modal.step === "2fa") {
                          /* go back to otp handled by parent */
                        }
                      })()
                    : null
                }
                className="p-1 text-gray-400 hover:text-gray-600 transition"
              />
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="text-center mb-6">
          <div
            className={`w-16 h-16 ${cfg.iconBg} rounded-full flex items-center justify-center mx-auto mb-4`}
          >
            {cfg.icon}
          </div>
          <h3 className="font-bold text-2xl text-gray-900 mb-2">{cfg.title}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{cfg.subtitle}</p>
        </div>

        <div className="space-y-4">
          {modal.step === "phone" && (
            <div className="w-full rounded-xl border border-gray-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition px-3 py-2">
              
              <PhoneInput
                international
                countryCallingCodeEditable={false}
                defaultCountry={defaultCountry}
                value={phone || undefined}
                onChange={(value) => onPhoneChange(value || "")}
                onKeyDown={handleKeyDown}
                placeholder="+1 234 567 8900"
                autoFocus
                limitMaxLength
                numberInputProps={{
                  className:
                    "w-full bg-transparent outline-none text-gray-800 placeholder:text-gray-400",
                }}
                countrySelectProps={{
                  className:
                    "mr-2 bg-transparent outline-none text-gray-700",
                }}
              />
            </div>
          )}

          {modal.step === "otp" && (
            <input
              type="text"
              value={otp}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 5);
                onOtpChange(val);
              }}
              onKeyDown={handleKeyDown}
              placeholder="5-digit code"
              maxLength={5}
              autoFocus
              className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition text-gray-800 text-center text-xl tracking-widest font-mono"
            />
          )}

          {modal.step === "2fa" && (
            <input
              type="password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="2FA password"
              autoFocus
              className="w-full h-12 px-4 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none transition text-gray-800"
            />
          )}

          {error && (
            <p className="text-sm text-red-500 text-center">{error}</p>
          )}

          <button
            onClick={
              modal.step === "phone"
                ? onPhoneSubmit
                : modal.step === "otp"
                  ? onOtpSubmit
                  : onPasswordSubmit
            }
            disabled={
              loading ||
              (modal.step === "phone" && !isPhoneValid) ||
              (modal.step === "otp" && otp.length < 5) ||
              (modal.step === "2fa" && !password.trim())
            }
            className="w-full h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Verifying...</span>
              </>
            ) : (
              <span>
                {modal.step === "phone"
                  ? "Send Code"
                  : modal.step === "otp"
                    ? "Verify Code"
                    : "Confirm"}
              </span>
            )}
          </button>
        </div>

        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            {(modal.step === "2fa" ? ["phone", "otp", "2fa"] : ["phone", "otp"]).map((s, i, arr) => (
              <div
                key={s}
                className={`h-2 rounded-full transition-all duration-300 ${
                  modal.step === s
                    ? "bg-blue-500 w-4"
                    : arr.indexOf(modal.step) > i
                      ? "bg-blue-200 w-2"
                      : "bg-gray-200 w-2"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
